import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
    inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';

type PlayerState = 'idle' | 'playing' | 'paused' | 'ended';

const WISTIA_SCRIPT_URL = 'https://fast.wistia.net/assets/external/E-v1.js';

// Module-level singleton: resolves once the script has fully loaded.
// All component instances share the same promise, avoiding duplicate loads
// and the race condition where a script tag exists but hasn't loaded yet.
let wistiaScriptPromise: Promise<void> | null = null;

function loadWistiaScript(): Promise<void> {
    if (!wistiaScriptPromise) {
        wistiaScriptPromise = new Promise<void>((resolve, reject) => {
            const script = document.createElement('script');
            script.src = WISTIA_SCRIPT_URL;
            script.async = true;
            script.onload = () => resolve();
            script.onerror = err => {
                wistiaScriptPromise = null; // allow retry on next mount
                reject(err);
            };
            document.body.appendChild(script);
        });
    }
    return wistiaScriptPromise;
}

@Component({
    selector: 'dr-video-player',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './video-player.component.html',
    styleUrls: ['./video-player.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VideoPlayerComponent implements OnInit, OnDestroy {
    @Input() videoUrl?: string;
    @Input() title = 'Watch video';
    @Input() posterLogoUrl = 'https://shared-assets.datarails.com/dr-assets/images/datarails-light.svg';
    @Input() faviconUrl = 'https://static.datarails.com/static/client/img/favicon/favicon_new.png';

    @ViewChild('videoEl') private videoRef?: ElementRef<HTMLVideoElement>;

    private readonly cdr = inject(ChangeDetectorRef);

    wistiaScriptReady = false;
    wistiaScriptError = false;

    state: PlayerState = 'idle';
    controlsVisible = false;

    currentTime = 0;
    duration = 0;
    buffered = 0;

    volume = 1;
    muted = false;
    isFullscreen = false;

    private controlsHideTimer: any;

    // ── Wistia ────────────────────────────────────────────────────────────────

    get isWistia(): boolean {
        return !!this.videoUrl?.includes('wistia.com');
    }

    get wistiaVideoId(): string | null {
        if (!this.isWistia || !this.videoUrl) return null;
        const match = this.videoUrl.match(/\/medias\/([^/?#]+)/);
        return match ? match[1] : null;
    }

    ngOnInit(): void {
        if (this.isWistia) {
            loadWistiaScript()
                .then(() => {
                    this.wistiaScriptReady = true;
                    this.cdr.markForCheck();
                })
                .catch(() => {
                    this.wistiaScriptError = true;
                    this.cdr.markForCheck();
                });
        }
    }

    // ── Getters ───────────────────────────────────────────────────────────────

    get progressPercent(): number {
        return this.duration ? (this.currentTime / this.duration) * 100 : 0;
    }

    get bufferedPercent(): number {
        return this.duration ? (this.buffered / this.duration) * 100 : 0;
    }

    get formattedCurrent(): string {
        return this.formatTime(this.currentTime);
    }
    get formattedDuration(): string {
        return this.formatTime(this.duration);
    }
    get isIdle(): boolean {
        return this.state === 'idle';
    }
    get isPlaying(): boolean {
        return this.state === 'playing';
    }

    // ── Poster click ──────────────────────────────────────────────────────────

    onPosterClick(): void {
        if (!this.videoUrl) return;
        this.state = 'playing';
        this.cdr.detectChanges();
        this.videoRef?.nativeElement.play();
    }

    // ── Video events ──────────────────────────────────────────────────────────

    onLoadedMetadata(event: Event): void {
        this.duration = (event.target as HTMLVideoElement).duration;
        this.cdr.markForCheck();
    }

    onTimeUpdate(event: Event): void {
        const video = event.target as HTMLVideoElement;
        this.currentTime = video.currentTime;
        if (video.buffered.length) {
            this.buffered = video.buffered.end(video.buffered.length - 1);
        }
        this.cdr.markForCheck();
    }

    onEnded(): void {
        this.state = 'ended';
        this.cdr.markForCheck();
    }

    onVideoPause(): void {
        if (this.state !== 'ended') {
            this.state = 'paused';
            this.cdr.markForCheck();
        }
    }

    onVideoPlay(): void {
        this.state = 'playing';
        this.cdr.markForCheck();
    }

    // ── Controls ──────────────────────────────────────────────────────────────

    togglePlay(): void {
        const video = this.videoRef?.nativeElement;
        if (!video) return;
        if (this.state === 'ended') {
            video.currentTime = 0;
            video.play();
        } else if (this.isPlaying) {
            video.pause();
        } else {
            video.play();
        }
    }

    seekTo(event: MouseEvent): void {
        const video = this.videoRef?.nativeElement;
        if (!video || !this.duration) return;
        const bar = event.currentTarget as HTMLElement;
        video.currentTime = (event.offsetX / bar.offsetWidth) * this.duration;
    }

    setVolume(event: Event): void {
        const video = this.videoRef?.nativeElement;
        if (!video) return;
        const value = parseFloat((event.target as HTMLInputElement).value);
        this.volume = value;
        video.volume = value;
        this.muted = value === 0;
        this.cdr.markForCheck();
    }

    toggleMute(): void {
        const video = this.videoRef?.nativeElement;
        if (!video) return;
        this.muted = !this.muted;
        video.muted = this.muted;
        this.cdr.markForCheck();
    }

    toggleFullscreen(container: HTMLElement): void {
        if (!document.fullscreenElement) {
            container.requestFullscreen();
            this.isFullscreen = true;
        } else {
            document.exitFullscreen();
            this.isFullscreen = false;
        }
        this.cdr.markForCheck();
    }

    showControls(): void {
        this.controlsVisible = true;
        clearTimeout(this.controlsHideTimer);
        if (this.isPlaying) {
            this.controlsHideTimer = setTimeout(() => {
                this.controlsVisible = false;
                this.cdr.markForCheck();
            }, 2500);
        }
        this.cdr.markForCheck();
    }

    hideControls(): void {
        if (this.isPlaying) {
            this.controlsHideTimer = setTimeout(() => {
                this.controlsVisible = false;
                this.cdr.markForCheck();
            }, 500);
        }
    }

    ngOnDestroy(): void {
        clearTimeout(this.controlsHideTimer);
    }

    private formatTime(seconds: number): string {
        if (!seconds || isNaN(seconds)) return '0:00';
        const m = Math.floor(seconds / 60);
        const s = Math.floor(seconds % 60);
        return `${m}:${s.toString().padStart(2, '0')}`;
    }
}
